import { useSyncExternalStore } from 'react';

/**
 * Запуск просмотра — от нажатия до первого кадра.
 *
 * Раньше между «нажал серию» и плеером на странице висела тихая строка
 * «Подготовка…» под списком, и нажатие выглядело как зависание. Теперь у
 * запуска есть свой слой поверх всего приложения (`LaunchLayer.tsx`): он
 * появляется в ответ на нажатие и живёт до первого кадра, а экран под ним за
 * это время меняется со страницы тайтла на плеер.
 *
 * Состояние слоя — здесь, вне страниц: страница, начавшая запуск, уходит
 * вместе со своим маршрутом, а слой должен её пережить. Отсюда и три участника:
 *
 * - страница начинает запуск (`beginLaunch`), сообщает этапы и либо передаёт
 *   его плееру (`handoff`), либо отказывается (`fail`);
 * - плеер подхватывает запуск по номеру сессии (`use-player-launch.ts`):
 *   дописывает этапы и снимает слой, когда ему есть что показать;
 * - оболочка сообщает о смене маршрута (`launchRouteCommitted`) — так слой
 *   узнаёт, что его экран ушёл не тем путём, которым он его вёл.
 *
 * Фазы:
 *   preparing — страница под слоем, сессия ещё создаётся;
 *   handoff   — сессия есть, под слоем уже плеер, ждём первый кадр;
 *   revealing — первый кадр пошёл, слой растворяется над видео;
 *   failed    — запуск не удался: слой говорит почему и уходит сам;
 *   closing   — отмена или отказ: слой уходит, страница возвращается.
 */
export type LaunchPhase = 'preparing' | 'handoff' | 'revealing' | 'failed' | 'closing';

export interface LaunchView {
  readonly id: number;
  readonly phase: LaunchPhase;
  /** Что запускается: «Сезон 1, Эпизод 4 · Название серии» или название фильма. */
  readonly label: string;
  /** Этап — теми словами, какими его называет страница или плеер. */
  readonly stage: string | null;
  /** Цифры движка под этапом (раздающие, скорость) — их знает только плеер. */
  readonly detail: string | null;
  readonly sessionId: string | null;
  /**
   * Что было в фокусе в миг нажатия — нажатый элемент. Запоминается здесь, а
   * не в слое: к его первому рендеру страница уже отключила нажатую кнопку
   * («занято»), и браузер снял с неё фокус.
   */
  readonly origin: HTMLElement | null;
  /** Вернуть ли фокус на нажатый элемент, когда слой уйдёт. */
  readonly returnFocus: boolean;
}

/** Запуск глазами страницы, которая его начала. */
export interface Launch {
  /** Отмена запросов запуска: зритель передумал — сервер не должен готовить поток зря. */
  readonly signal: AbortSignal;
  /** Запуск отменён (или вытеснен следующим): в плеер уходить уже нельзя. */
  readonly cancelled: boolean;
  /** Новый этап. Слова — те, что страница показывала и раньше. */
  stage(text: string): void;
  /**
   * Сессия создана: слой становится экраном загрузки плеера, адрес меняется на
   * плеер. `abandon` — что сделать с сессией, если зритель отменит запуск
   * раньше, чем плеер успеет появиться и взять её на себя.
   */
  handoff(sessionId: string, hash: string, abandon?: () => void): void;
  /**
   * Запуск не удался: слой уходит, страница и фокус возвращаются.
   *
   * `message` — почему. Страница показывает причину у себя, как и раньше, но
   * её строка стоит под списком и часто за краем экрана: слой исчез бы молча,
   * и нажатие снова выглядело бы пропавшим. Поэтому причину сперва говорит сам
   * слой — на месте этапа, — держит её столько, сколько нужно на чтение
   * (`failHoldMs`), и уходит; любое нажатие убирает его сразу.
   */
  fail(message?: string): void;
}

interface Running {
  view: LaunchView;
  readonly abort: AbortController;
  readonly onCancel: (() => void) | undefined;
  /** «Назад» плеера: в фазе handoff уходить с экрана умеет только он. */
  back: (() => void) | null;
  /** Закрыть сессию, которую плеер ещё не успел подхватить. */
  abandon: (() => void) | null;
  /** Адрес, с которого начали: смена его не нами — отмена. */
  readonly startHash: string;
  /** В истории лежит наша запись-страж (см. `pushGuard`). */
  guard: boolean;
  cancelled: boolean;
  /** Таймер ухода слоя, показывающего причину отказа. */
  holdTimer: number;
}

/**
 * Сколько слой держит причину отказа, мс: от двух с половиной секунд на
 * короткую фразу до шести на длинную — примерно со скоростью чтения.
 */
export function failHoldMs(message: string): number {
  return Math.min(6000, Math.max(2500, message.length * 70));
}

/** Сколько слой растворяется над первым кадром и сколько уходит при отмене, мс. */
export const LAUNCH_REVEAL_MS = 400;
export const LAUNCH_CLOSE_MS = 200;

let running: Running | null = null;
let nextId = 1;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getView(): LaunchView | null {
  return running?.view ?? null;
}

/** Что сейчас на слое; `null` — слоя нет. */
export function useLaunchView(): LaunchView | null {
  return useSyncExternalStore(subscribe, getView, getView);
}

/** То же вне React: проверкам и плееру на первом рендере. */
export function currentLaunch(): LaunchView | null {
  return getView();
}

function patch(target: Running, change: Partial<LaunchView>): void {
  if (running !== target) return;
  target.view = { ...target.view, ...change };
  emit();
}

/* ---------- «Назад» браузера отменяет запуск, а не уводит со страницы ----------
 *
 * Шаги карточки живут в адресе, и «назад» во время подготовки увёл бы зрителя
 * на шаг раньше — со списка серий к источникам, — хотя хотел он одного:
 * отменить. Поэтому на время подготовки в историю кладётся запись-страж с тем
 * же адресом: «назад» снимает её (адрес не меняется, страница остаётся на
 * месте), а мы по этому событию отменяем запуск.
 *
 * Запись не должна пережить запуск. Удачный уход в плеер ЗАМЕНЯЕТ её адресом
 * плеера — история получается той же, что и раньше: страница → плеер. Отмена
 * и отказ снимают её сами (`history.back()`); событие от этого шага приходит
 * позже, и его надо пропустить, а не принять за новое «назад».
 */
let skipPops = 0;
let historyHooked = false;

function hookHistory(): void {
  if (historyHooked || typeof window === 'undefined') return;
  historyHooked = true;
  window.addEventListener('popstate', () => {
    if (skipPops > 0) {
      skipPops -= 1;
      return;
    }
    const target = running;
    if (target === null || !target.guard) return;
    const phase = target.view.phase;
    if (phase !== 'preparing' && phase !== 'failed') return;
    // Страж уже снят самим «назад» — второй раз его не трогаем.
    target.guard = false;
    cancel(target);
  });
}

function pushGuard(): boolean {
  // Прошлый страж ещё снимается: вторая запись поверх него осталась бы в
  // истории навсегда. Этот запуск обойдётся без стража — «назад» отменит его
  // вместе с шагом страницы, как было бы и без слоя.
  if (skipPops > 0) return false;
  try {
    hookHistory();
    window.history.pushState(window.history.state, '');
    return true;
  } catch {
    return false;
  }
}

function dropGuard(target: Running): void {
  if (!target.guard) return;
  target.guard = false;
  skipPops += 1;
  try {
    window.history.back();
  } catch {
    skipPops -= 1;
  }
}

/* ---------- Переход на плеер ----------
 *
 * Адрес меняется сразу — плеер должен начать работу в тот же миг, что и
 * раньше. Но под слоем при этом одним кадром меняется всё: размытая страница
 * на чёрный экран плеера. Чтобы это было растворением, а не щелчком, смену
 * экрана отдаём браузеру (View Transitions): он снимает страницу как была и
 * растворяет снимок в новом экране, пока тот уже живёт. Знак и подписи слоя в
 * снимок не входят (у них своё имя перехода, styles.css) — они всё это время
 * живые и стоят на месте.
 *
 * Нет такой возможности у браузера или просили меньше движения — экран
 * меняется сразу. Там же, где нет и размытия, слой плотный, и щелчка под ним
 * не видно.
 */
type ViewTransition = { ready?: Promise<unknown>; finished?: Promise<unknown>; updateCallbackDone?: Promise<unknown> };
type TransitionStarter = (update: () => Promise<void>) => ViewTransition;

let routeCommitted: (() => void) | null = null;
/** Сколько ждём, пока оболочка отрисует новый маршрут, прежде чем отпустить переход, мс. */
const ROUTE_COMMIT_WAIT_MS = 150;

function calm(): boolean {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function crossfade(navigate: () => void): void {
  const start = (document as unknown as { startViewTransition?: TransitionStarter }).startViewTransition;
  if (typeof start !== 'function' || calm()) {
    navigate();
    return;
  }
  const root = document.documentElement;
  let navigated = false;
  const run = (): void => {
    if (navigated) return;
    navigated = true;
    navigate();
  };
  try {
    root.classList.add('launch-handoff');
    const transition = start.call(document, () => new Promise<void>((resolve) => {
      const done = (): void => {
        routeCommitted = null;
        resolve();
      };
      routeCommitted = done;
      window.setTimeout(done, ROUTE_COMMIT_WAIT_MS);
      run();
    }));
    const settle = (): void => root.classList.remove('launch-handoff');
    // Переход могут пропустить (вкладка скрыта, начат следующий) — это не
    // ошибка: экран уже сменён, а отказ обещания нас не касается.
    void Promise.resolve(transition.finished).then(settle, settle);
    void Promise.resolve(transition.ready).catch(() => undefined);
    void Promise.resolve(transition.updateCallbackDone).catch(() => undefined);
  } catch {
    root.classList.remove('launch-handoff');
    run();
  }
}

/* ---------- Запуск ---------- */

function focused(): HTMLElement | null {
  const active = document.activeElement;
  return active instanceof HTMLElement && active !== document.body ? active : null;
}

/** Слой с причиной отказа уходит: время вышло или зритель нажал что угодно. */
function dismiss(target: Running): void {
  if (running !== target || target.view.phase !== 'failed') return;
  window.clearTimeout(target.holdTimer);
  patch(target, { phase: 'closing', returnFocus: true });
  dropGuard(target);
}

function cancel(target: Running): void {
  if (running !== target) return;
  const phase = target.view.phase;
  if (phase === 'failed') {
    dismiss(target);
    return;
  }
  if (target.cancelled || phase === 'revealing' || phase === 'closing') return;
  target.cancelled = true;
  target.abort.abort();
  if (phase === 'handoff') {
    // Под слоем уже плеер: уйти с его экрана умеет он сам (досылает позицию,
    // закрывает сессию, возвращает на карточку). Не успел смонтироваться —
    // возвращаемся шагом истории: запись плеера лежит сразу за карточкой.
    patch(target, { phase: 'closing', returnFocus: false });
    if (target.back !== null) {
      target.back();
      return;
    }
    target.abandon?.();
    window.history.back();
    return;
  }
  patch(target, { phase: 'closing', returnFocus: true });
  dropGuard(target);
  target.onCancel?.();
}

/**
 * Начать запуск: слой появляется немедленно, в том же кадре, что и нажатие.
 * Звать ДО первого `await` обработчика — иначе отклик опоздает на запрос.
 */
export function beginLaunch(options: {
  label: string;
  stage?: string;
  /** Зритель отменил запуск (Esc, «назад»): странице пора снять свою занятость. */
  onCancel?: () => void;
}): Launch {
  // Предыдущий запуск ещё не закончился — такого быть не должно (слой закрывает
  // страницу), но если случилось, прежний тихо отменяется: два запуска разом
  // закончились бы двумя плеерами.
  const previous = running;
  if (previous !== null) {
    window.clearTimeout(previous.holdTimer);
    if (!previous.cancelled) {
      previous.cancelled = true;
      previous.abort.abort();
    }
  }
  const target: Running = {
    view: {
      id: nextId,
      phase: 'preparing',
      label: options.label,
      stage: options.stage ?? null,
      detail: null,
      sessionId: null,
      origin: focused(),
      returnFocus: false,
    },
    abort: new AbortController(),
    onCancel: options.onCancel,
    back: null,
    abandon: null,
    startHash: window.location.hash,
    // Страж прежнего запуска достаётся новому: адрес у них один.
    guard: previous?.guard === true ? true : pushGuard(),
    cancelled: false,
    holdTimer: 0,
  };
  if (previous !== null) previous.guard = false;
  nextId += 1;
  running = target;
  emit();
  return {
    signal: target.abort.signal,
    get cancelled() {
      return target.cancelled || running !== target;
    },
    stage(text) {
      if (target.view.phase === 'preparing') patch(target, { stage: text });
    },
    handoff(sessionId, hash, abandon) {
      if (running !== target || target.cancelled || target.view.phase !== 'preparing') return;
      // Фаза меняется ВНУТРИ перехода, вместе с адресом: браузер снимает
      // прежний экран до этого, и на снимке должна быть ещё страница под
      // слоем, а не уже чёрный фон плеера — иначе растворять было бы нечего.
      crossfade(() => {
        // Между просьбой и снимком проходит кадр — зритель мог успеть отменить.
        if (running !== target || target.cancelled || target.view.phase !== 'preparing') {
          abandon?.();
          return;
        }
        target.abandon = abandon ?? null;
        const replace = target.guard;
        target.guard = false;
        patch(target, { phase: 'handoff', sessionId });
        if (replace) window.location.replace(hash);
        else window.location.hash = hash;
      });
    },
    fail(message) {
      if (running !== target || target.cancelled || target.view.phase !== 'preparing') return;
      target.cancelled = true;
      const reason = message?.trim() ?? '';
      if (reason === '') {
        patch(target, { phase: 'closing', returnFocus: true });
        dropGuard(target);
        return;
      }
      patch(target, { phase: 'failed', stage: reason, detail: null });
      target.holdTimer = window.setTimeout(() => dismiss(target), failHoldMs(reason));
    },
  };
}

/** Зритель отменяет запуск: Esc, кнопка «назад» на слое. Слой с причиной отказа — убирает. */
export function cancelLaunch(): void {
  if (running !== null) cancel(running);
}

/** Слой доиграл свой уход и убран с экрана. */
export function finishLaunch(id: number): void {
  if (running === null || running.view.id !== id) return;
  const phase = running.view.phase;
  if (phase !== 'revealing' && phase !== 'closing') return;
  running = null;
  emit();
}

/**
 * Оболочка отрисовала маршрут. Здесь же замечаем, что экран под слоем сменился
 * не нами: «вперёд»/«назад» браузера, чужая ссылка.
 */
export function launchRouteCommitted(hash: string): void {
  routeCommitted?.();
  const target = running;
  if (target === null) return;
  const { phase, sessionId } = target.view;
  if (phase === 'preparing' || phase === 'failed') {
    if (hash === target.startHash) return;
    // Адрес сменился под нами — страж в истории уже не на вершине, не трогаем.
    target.guard = false;
    cancel(target);
    return;
  }
  if (phase === 'closing' || sessionId === null) return;
  if (hash.startsWith(`#/watch/${encodeURIComponent(sessionId)}`)) return;
  // Ушли с плеера этого запуска (или он сменил сессию) — слой больше ничей.
  target.cancelled = true;
  patch(target, { phase: 'closing', returnFocus: false });
}

/* ---------- Плеер ---------- */

function adopted(sessionId: string): Running | null {
  return running !== null && running.view.sessionId === sessionId && running.view.phase === 'handoff'
    ? running
    : null;
}

/** Плеер ещё ждёт: что именно — теми же словами, что на его экране загрузки. */
export function playerLaunchStage(sessionId: string, stage: string, detail: string | null): void {
  const target = adopted(sessionId);
  if (target === null) return;
  if (target.view.stage === stage && target.view.detail === detail) return;
  patch(target, { stage, detail });
}

/** Первый кадр готов: слой растворяется над видео. */
export function playerLaunchReveal(sessionId: string): void {
  const target = adopted(sessionId);
  if (target !== null) patch(target, { phase: 'revealing' });
}

/** Плеер показывает отказ: слой ему мешает и уходит сразу. */
export function playerLaunchDrop(sessionId: string): void {
  const target = adopted(sessionId);
  if (target === null) return;
  target.cancelled = true;
  patch(target, { phase: 'closing', returnFocus: false });
}

/** «Назад» плеера — для кнопки и Esc на слое, пока он закрывает плеер. */
export function playerLaunchBack(sessionId: string, back: (() => void) | null): void {
  if (running !== null && running.view.sessionId === sessionId) running.back = back;
}

/** Только для проверок: вернуть модуль в исходное состояние. */
export function resetLaunchForTests(): void {
  if (running !== null) window.clearTimeout(running.holdTimer);
  running = null;
  routeCommitted = null;
  skipPops = 0;
  emit();
}
